import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/header/Header';
import Footer from '../components/footer/Footer';

function TermsAndConditions() {
	const navigate = useNavigate();
	const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
	const [showButtons, setShowButtons] = useState(false);

	useEffect(() => {
		const handleScroll = () => {
			const element = document.getElementById('terms-content');
			if (element) {
				const { scrollTop, scrollHeight, clientHeight } = element;
				const isScrolledToBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px threshold
				setHasScrolledToBottom(isScrolledToBottom);
			}
		};

		const element = document.getElementById('terms-content');
		if (element) {
			element.addEventListener('scroll', handleScroll);
			return () => element.removeEventListener('scroll', handleScroll);
		}
	}, []);

	useEffect(() => {
		if (hasScrolledToBottom) {
			const timer = setTimeout(() => {
				setShowButtons(true);
			}, 500); // Delay to show buttons after reaching bottom
			return () => clearTimeout(timer);
		}
	}, [hasScrolledToBottom]);

	const handleAccept = (e) => {
		e.preventDefault(); // Prevent any default behavior
		e.stopPropagation(); // Prevent event bubbling
		
		// Store acceptance in localStorage with a flag indicating user just accepted
		localStorage.setItem('termsAccepted', 'true');
		localStorage.setItem('termsAcceptedDate', new Date().toISOString());
		localStorage.setItem('termsJustAccepted', 'true'); // Flag for auto-checking
		
		// Redirect to signup page
		navigate('/signup');
	};

	const handleReject = () => {
		// Navigate to homepage
		navigate('/');
	};

	return (
		<div className="min-h-screen bg-white dark:bg-gray-900">
			<Header />
			<main className="max-w-4xl mx-auto p-6">
				<div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
					<div className="bg-blue-600 text-white p-6">
						<h1 className="text-3xl font-bold">ValuePay.ai</h1>
						<h2 className="text-xl mt-2">Website Terms and Conditions</h2>
					</div>
					
					<div 
						id="terms-content"
						className="p-6 h-96 overflow-y-auto border border-gray-200 dark:border-gray-600"
						style={{ maxHeight: '400px' }}
					>
						<div className="prose dark:prose-invert max-w-none">
							<p className="text-lg mb-4">
								Welcome to ValuePay.ai! Before you access or use our website located at 
								<a href="https://www.valuepay.ai" className="text-blue-600 hover:underline"> https://www.valuepay.ai </a>
								("Website"), please read these Terms and Conditions ("Terms") carefully.
							</p>
							
							<div className="bg-yellow-50 dark:bg-yellow-900 border-l-4 border-yellow-400 p-4 my-6">
								<p className="text-sm text-yellow-800 dark:text-yellow-200">
									<strong>The Accept button will only become available once you have scrolled through the text.</strong> 
									Clicking Reject will prevent access to the Website.
								</p>
							</div>

							<div className="border-t-2 border-gray-300 pt-6 mt-6">
								<h3 className="text-lg font-bold text-red-600 mb-4">
									PLEASE READ CAREFULLY BEFORE ACCESSING OR USING THIS WEBSITE:
								</h3>
								<p className="mb-4">
									This Terms and Conditions document ("Agreement") is a legal agreement between you ("User", "you" or "your") 
									and ValuePay.ai ("Company", "we", "our" or "us"), governing your use of the Website and any related services 
									or software provided by the Company.
								</p>
								<p className="mb-4">
									Your use of the Website constitutes your acceptance of these Terms. If you do not agree to these Terms, 
									you must not use or access the Website.
								</p>
							</div>

							<div className="bg-blue-50 dark:bg-blue-900 border-l-4 border-blue-400 p-4 my-6">
								<h3 className="font-bold text-blue-800 dark:text-blue-200 mb-2">IMPORTANT NOTICE TO USERS:</h3>
								<p className="text-sm text-blue-800 dark:text-blue-200">
									WE ARE A BUSINESS-TO-BUSINESS SERVICE PROVIDER. THIS WEBSITE IS INTENDED FOR USE BY BUSINESS USERS 
									AND NOT BY PRIVATE INDIVIDUALS ACTING AS CONSUMERS. NOTWITHSTANDING THIS, NOTHING IN THESE TERMS 
									AFFECTS THE STATUTORY RIGHTS OF ANY USER DEALING AS A CONSUMER.
								</p>
							</div>

							<h3 className="text-xl font-bold mt-8 mb-4">1. Definitions</h3>
							<ul className="list-disc pl-6 mb-6">
								<li>"Services" means the services provided by ValuePay.ai via the Website, including any software or AI-powered features.</li>
								<li>"Software" means any online software applications offered as part of the Services.</li>
								<li>"User Data" means any data, content, or materials you upload, input, or generate via the Website.</li>
								<li>"Effective Date" means the date you accept these Terms by clicking "Accept".</li>
							</ul>

							<h3 className="text-xl font-bold mt-8 mb-4">2. Licence to Use the Website and Services</h3>
							<p className="mb-4">
								Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, 
								revocable licence to access and use the Website and Services for your internal business purposes only.
							</p>
							<p className="mb-2">You agree not to:</p>
							<ul className="list-disc pl-6 mb-6">
								<li>Use the Website or Services for unlawful purposes or in any manner that could damage or impair our operations.</li>
								<li>Attempt to gain unauthorized access to any systems or data.</li>
								<li>Introduce viruses, malware, or harmful code.</li>
								<li>Copy, modify, distribute, or create derivative works of our content or software without express written permission.</li>
								<li>Use the Website or Services to create competitive products or services.</li>
							</ul>

							<h3 className="text-xl font-bold mt-8 mb-4">3. Experimental Generative AI Features</h3>
							<p className="mb-4">
								The Website includes features powered by Generative Artificial Intelligence ("AI") technology, 
								provided on an experimental basis for informational use only.
							</p>
							<p className="mb-2">You acknowledge that:</p>
							<ul className="list-disc pl-6 mb-4">
								<li>AI-generated content may be incomplete, inaccurate, or unreliable.</li>
								<li>We do not guarantee the accuracy or suitability of AI-generated outputs.</li>
								<li>You should independently verify any AI-generated information before relying on it.</li>
								<li>We disclaim all liability for any damages resulting from your use of AI features.</li>
								<li>The AI features may be modified, suspended, or discontinued at any time without notice.</li>
							</ul>
							<p className="mb-6 font-semibold text-red-600">You use these features at your own risk.</p>

							<h3 className="text-xl font-bold mt-8 mb-4">4. Intellectual Property Rights</h3>
							<p className="mb-4">
								All intellectual property rights in the Website, Services, and Software, including text, graphics, 
								logos, and AI-generated content, remain the property of the Company or its licensors.
							</p>
							<p className="mb-6">You agree not to remove or alter any copyright, trademark, or proprietary notices.</p>

							<h3 className="text-xl font-bold mt-8 mb-4">5. Privacy and Data Protection</h3>
							<p className="mb-4">
								We comply with the UK GDPR and Data Protection Act 2018. Our Privacy Policy explains how we collect, 
								use, store, and protect your personal data.
							</p>
							<p className="mb-4">
								By using the Website, you consent to our collection and processing of your personal data as described 
								in the Privacy Policy.
							</p>
							<p className="mb-6">
								You have rights under data protection laws, including rights to access, correct, erase, restrict, 
								or object to processing of your data. Please contact us at [Insert Contact Email] to exercise these rights.
							</p>

							<h3 className="text-xl font-bold mt-8 mb-4">6. Cookies</h3>
							<p className="mb-4">
								Our Website uses cookies to improve user experience and analyse site traffic.
							</p>
							<p className="mb-4">
								By continuing to use the Website, you consent to our use of cookies as described in our Cookie Policy.
							</p>
							<p className="mb-6">
								You can manage or disable cookies via your browser settings; however, this may affect Website functionality.
							</p>

							<h3 className="text-xl font-bold mt-8 mb-4">7. No Warranty</h3>
							<p className="mb-4">
								The Website and Services are provided "as is" and "as available" without warranties of any kind.
							</p>
							<p className="mb-6">We do not guarantee uninterrupted or error-free access.</p>

							<h3 className="text-xl font-bold mt-8 mb-4">8. Limitation of Liability</h3>
							<p className="mb-4">To the fullest extent permitted by law, we exclude all liability for any:</p>
							<ul className="list-disc pl-6 mb-4">
								<li>Direct, indirect, incidental, consequential, or punitive damages.</li>
								<li>Loss of profits, data, business, or goodwill.</li>
								<li>Errors or omissions in content.</li>
								<li>Damage caused by viruses or malware.</li>
								<li>Third-party content or linked websites.</li>
							</ul>
							<p className="mb-4">
								Our total liability to you for any claim related to the Website or Services is limited to the amount 
								you have paid to us in the 12 months prior to the claim.
							</p>
							<p className="mb-6">
								Nothing in this clause excludes liability for death or personal injury caused by our negligence or for fraud.
							</p>

							<h3 className="text-xl font-bold mt-8 mb-4">9. Indemnity</h3>
							<p className="mb-6">
								You agree to indemnify and hold harmless the Company, its officers, employees, and agents against any losses, 
								damages, liabilities, costs, or expenses arising from your breach of these Terms or your use of the Website or Services.
							</p>

							<h3 className="text-xl font-bold mt-8 mb-4">10. Third-Party Links</h3>
							<p className="mb-6">
								We may provide links to third-party websites for your convenience. We do not control or endorse these sites 
								and are not responsible for their content or your dealings with them.
							</p>

							<h3 className="text-xl font-bold mt-8 mb-4">11. Termination</h3>
							<p className="mb-4">
								We may suspend or terminate your access to the Website or Services at any time, with or without cause or notice, 
								including for breach of these Terms.
							</p>
							<p className="mb-6">
								Upon termination, your licence ends immediately, and you must cease all use of the Website and Services.
							</p>

							<h3 className="text-xl font-bold mt-8 mb-4">12. Changes to Terms</h3>
							<p className="mb-4">
								We may update these Terms at any time by posting the updated version on the Website.
							</p>
							<p className="mb-6">Your continued use after changes means you accept the revised Terms.</p>

							<h3 className="text-xl font-bold mt-8 mb-4">13. Governing Law and Jurisdiction</h3>
							<p className="mb-4">These Terms are governed by the laws of England and Wales.</p>
							<p className="mb-6">
								Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.
							</p>

							<h3 className="text-xl font-bold mt-8 mb-4">14. Contact</h3>
							<p className="mb-8">
								For questions about these Terms, privacy, or data protection, please contact:
							</p>

							<div className="bg-gray-100 dark:bg-gray-700 p-6 rounded-lg text-center">
								<p className="font-bold text-lg mb-2">
									BY CLICKING "ACCEPT" YOU CONFIRM THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS AND CONDITIONS.
								</p>
							</div>
						</div>
					</div>

					<div className="p-6 bg-gray-50 dark:bg-gray-700">
						<div className="flex justify-center space-x-4">
							<button
								onClick={handleReject}
								className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors duration-200"
							>
								Reject
							</button>
							<button
								onClick={handleAccept}
								disabled={!showButtons}
								className={`px-6 py-3 font-medium rounded-lg transition-colors duration-200 ${
									showButtons
										? 'bg-green-600 hover:bg-green-700 text-white cursor-pointer'
										: 'bg-gray-300 text-gray-500 cursor-not-allowed'
								}`}
							>
								{showButtons ? 'Accept' : 'Scroll to Enable Accept'}
							</button>
						</div>
						{!showButtons && (
							<p className="text-center text-sm text-gray-600 dark:text-gray-400 mt-2">
								Please scroll through the entire document to enable the Accept button
							</p>
						)}
					</div>
				</div>
			</main>
			<Footer />
		</div>
	);
}

export default TermsAndConditions;